/**
 * Context for passing loader-fetched data to section components that
 * need it (e.g. BlogGrid needs pre-fetched blog posts).
 */
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";

export interface LeadFormSubmission {
  name: string;
  email: string;
  phone?: string;
  message?: string;
  customFields?: Array<{ label: string; value: string }>;
  honeypot?: string;
}

export type SubmitLeadForm = (data: LeadFormSubmission) => Promise<{ success: boolean }>;

export interface RenderContextValue {
  blogPosts: BlogPostEntry[];
  blogBasePath?: string;
  faqItems: FaqItemEntry[];
  bookingTypes: BookingTypeEntry[];
  isEditor?: boolean;
  /** Page-level attribution tag for lead source tagging (appended to booking links). */
  attribution?: string | null;
  /** URL-derived tag filter (?tag=slug) for pre-selecting a tag in BlogList. */
  urlTagFilter?: string | null;
  /** Submit handler for LeadForm sections. Absent in the editor canvas. */
  submitLeadForm?: SubmitLeadForm;
}

export interface BlogPostTag {
  name: string;
  slug: string;
}

export interface BlogPostEntry {
  id: string;
  title: string;
  slug: string;
  author?: string;
  summary?: string;
  publishAt?: string;
  featuredImageUrl?: string;
  tags?: BlogPostTag[];
}

export interface FaqItemEntry {
  id: string;
  question: string;
  answer: string;
  sortOrder?: number;
}

export interface BookingTypeEntry {
  id: string;
  name: string;
  description?: string;
  sortOrder?: number;
  slug?: string;
  minCustomers?: number;
  maxCustomers?: number;
  customerCountType?: string;
  startingPrice?: number | null;
  pricingPlan?: {
    name: string;
    authAmount: number;
    hourlyRate: number;
  };
}

const RenderCtx = createContext<RenderContextValue>({
  blogPosts: [],
  faqItems: [],
  bookingTypes: [],
  isEditor: false,
  attribution: null,
});

export function RenderContextProvider({
  value,
  children,
}: {
  value: RenderContextValue;
  children: ReactNode;
}) {
  const memoized = useMemo(
    () => value,
    [value.blogPosts, value.blogBasePath, value.faqItems, value.bookingTypes, value.isEditor, value.attribution, value.urlTagFilter, value.submitLeadForm],
  );
  return <RenderCtx.Provider value={memoized}>{children}</RenderCtx.Provider>;
}

export function useRenderContext(): RenderContextValue {
  return useContext(RenderCtx);
}
